import React, { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import styles from './Entry.module.css';
import InputField from '../../../../components/inputfield/InputField';
import AssessmentIcon from "@mui/icons-material/Assignment";
import SaveIcon from '@mui/icons-material/Save';
import TemplateIcon from '@mui/icons-material/InsertDriveFile';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import Chip from '@mui/material/Chip';
import CustomButton from '../../../../components/buttons/CustomButton';
import ModuleCard from '../moduleCard/ModuleCard';
import { DataProviderInstance } from '../../../../api/DataProvider';
import MessagesTemplate from '../../../../consts/MessagesTemplate';

const Entry = forwardRef(({ appointmentId, onAssesmentChange, onEvolutionChange }, ref) => {
    const KEY_ASSESSMENT = appointmentId+"_assessment";
    const KEY_EVOLUTION = appointmentId+"_evolution";

    const { CompanyConfig } = DataProviderInstance();
    const templates = MessagesTemplate.getTemplates(CompanyConfig);

    const [assessment, setAssessment] = useState("");
    const [evolution, setEvolution] = useState("");
    const [lastSaved, setLastSaved] = useState(null);

    const assessmentInputRef = React.createRef();
    const evolutionInputRef = React.createRef();

    const numLines = 4;

    useEffect(() => {
        const assessment = localStorage.getItem(KEY_ASSESSMENT);
        const evolution = localStorage.getItem(KEY_EVOLUTION);

        if (assessment) {
            onAssesmentChangeHandler(assessment);
        }

        if (evolution) {
            onEvolutionChangeHandler(evolution);
        }
    }, []);

    useEffect(() => {
        localStorage.setItem(KEY_ASSESSMENT, assessment);
        if (assessmentInputRef.current) {
            assessmentInputRef.current.SetValue(assessment);
        }
    }, [assessment]);

    useEffect(() => {
        localStorage.setItem(KEY_EVOLUTION, evolution);
        if (evolutionInputRef.current) {
            evolutionInputRef.current.SetValue(evolution);
        }
    }, [evolution]);

    useImperativeHandle(ref, () => ({
        Clear() {
            setAssessment("");
            setEvolution("");
            localStorage.removeItem(KEY_ASSESSMENT);
            localStorage.removeItem(KEY_EVOLUTION);
        }
    }));

    function onAssesmentChangeHandler(value) {
        setAssessment(value);
        onAssesmentChange(value);
    }

    function onEvolutionChangeHandler(value) {
        setEvolution(value);
        onEvolutionChange(value);
    }

    const handleSaveDraft = () => {
        localStorage.setItem(KEY_ASSESSMENT, assessment);
        localStorage.setItem(KEY_EVOLUTION, evolution);
        setLastSaved(new Date());
    };

    const getTemplateText = (type) => {
        let template;
        if (type == 'assessment') {
            template = templates.AppointmentAssessmentTemplate;
        } else {
            template = templates.AppointmentEvolutionTemplate;
        }
        
        // Converter <br /> para quebras de linha para o input field
        if (template) {
            return template.replace(/<br\s*\/?>/gi, '\n');
        }
        
        return template;
    };

    const insertTemplate = (type) => {
        const template = getTemplateText(type);
        
        if (type == 'assessment') {
            // Inserção inteligente: anexa ao final preservando texto existente
            const currentText = assessment.trim();
            const newText = currentText
                ? `${currentText}\n\n--- Modelo ---\n${template}`
                : template;
            onAssesmentChangeHandler(newText);
        } else {
            // Inserção inteligente: anexa ao final preservando texto existente
            const currentText = evolution.trim();
            const newText = currentText
                ? `${currentText}\n\n--- Modelo ---\n${template}`
                : template;
            onEvolutionChangeHandler(newText);
        }
    };

    const actions = (
        <>
            {lastSaved && (
                <Chip
                    icon={<SaveIcon fontSize="small" />}
                    label={`Salvo às ${lastSaved.toLocaleTimeString()}`}
                    size="small"
                    color="success"
                    variant="outlined"
                    className={styles.savedChip}
                />
            )}
            <CustomButton
                style="primary"
                variant="outlined"
                icon={<SaveIcon />}
                label="Salvar rascunho"
                onClick={handleSaveDraft}
            />
        </>
    );

    // Os dois campos são obrigatórios para registrar: ficam lado a lado (ou um
    // embaixo do outro em card estreito), cada um com o selo do que falta.
    const fields = [
        {
            key: 'assessment',
            title: 'Queixa do Paciente',
            tooltip: 'Descreva os sintomas, duração, intensidade e fatores que agravam ou aliviam',
            hint: 'Relato livre deste atendimento',
            label: 'Queixa',
            placeholder: 'Descreva detalhadamente a queixa do paciente...',
            value: assessment,
            inputRef: assessmentInputRef,
            onChange: onAssesmentChangeHandler,
        },
        {
            key: 'evolution',
            title: 'Evolução do Paciente',
            tooltip: 'Descreva os procedimentos realizados, resposta do paciente e recomendações',
            hint: 'O que foi feito hoje e como o paciente respondeu',
            label: 'Evolução',
            placeholder: 'Descreva os procedimentos realizados e a evolução do paciente...',
            value: evolution,
            inputRef: evolutionInputRef,
            onChange: onEvolutionChangeHandler,
        },
    ];

    return (
        <ModuleCard
            accent="blue"
            title="Registro de Atendimento"
            description="Preencha a queixa e a evolução para registrar o atendimento"
            icon={<AssessmentIcon />}
            actions={actions}
        >
            <div className={styles.fields}>
                {fields.map((field) => {
                    const filled = field.value !== '';
                    return (
                        <div key={field.key} className={styles.itemContainer}>
                            <div className={styles.fieldHeader}>
                                <div className={styles.fieldTitle}>
                                    <span>{field.title}</span>
                                    <Tooltip title={field.tooltip}>
                                        <InfoIcon fontSize="small" color="action" />
                                    </Tooltip>
                                    <span className={`${styles.fieldStatus} ${filled ? styles.fieldStatusDone : styles.fieldStatusMissing}`}>
                                        {filled ? 'Preenchido' : 'Falta preencher'}
                                    </span>
                                </div>

                                <CustomButton
                                    style="primary"
                                    variant="text"
                                    icon={<TemplateIcon fontSize="small" />}
                                    label="Inserir modelo"
                                    onClick={() => insertTemplate(field.key)}
                                />
                            </div>
                            <div className={styles.fieldHint}>{field.hint}</div>

                            <InputField
                                ref={field.inputRef}
                                label={field.label}
                                multiline
                                rows={numLines}
                                OnUpdateValue={field.onChange}
                                placeholder={field.placeholder}
                            />
                            <div className={styles.charCount}>
                                {field.value.length} caracteres
                            </div>
                        </div>
                    );
                })}
            </div>
        </ModuleCard>
    );
});

export default Entry;